<template>
  <BContainer
    class="p-0"
    fluid
  >
    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        class="mb-2 d-flex gap-2"
      >
        <BButton
          variant="primary"
          size="sm"
          @click.prevent="createDocument"
        >
          Add Document
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableDocuments"
      table-class="min-h-375px"
      :provider="tableProvider"
      :fields="tableDocuments.fields"
      :filter="tableDocuments.filter"
      :filter-on="tableDocuments.filterOn"
      :sort-by="tableDocuments.sortBy"
      :sort-desc="tableDocuments.sortDesc"
      :sort-direction="tableDocuments.sortDirection"
      :total-rows="tableDocuments.totalRows"
      :current-page="tableDocuments.currentPage"
      :per-page="tableDocuments.perPage"
      :stacked="tableDocuments.stacked"
      :busy="tableDocuments.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="warning"
            :disabled="tableDocuments.busy"
            @click.prevent="viewDocument(item)"
          >
            View
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalDocument"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <div class="p-2 min-h-400px max-h-400px">
        <ValidationObserver
          ref="formDocument"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <BRow>
              <BCol
                cols="12"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="document_type_id"
                  name="document type"
                  rules="required"
                >
                  <BFormGroup
                    label="Document Type"
                    label-for="fDocumentType"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <VSelect
                      v-model="selected.documentType"
                      input-id="fDocumentType"
                      label="document_type_name"
                      clearable
                      append-to-body
                      placeholder="select document type here"
                      :options="options.documentTypes"
                      :disabled="fetching.documentTypes || state.editing"
                      :loading="fetching.documentTypes"
                      :calculate-position="calculateDropPosition"
                      :class="{ 'is-invalid': errors.length > 0 }"
                      @search="searchDocumentTypes"
                    >
                      <template #option="{ document_type_name }">
                        <div
                          class="text-break text-wrap"
                          v-text="document_type_name"
                        />
                      </template>
                      <template #selected-option="{ document_type_name }">
                        <div
                          class="w-225px w-md-325px text-truncate"
                          v-text="document_type_name"
                        />
                      </template>
                      <template #no-options="">
                        no available document types
                      </template>
                    </VSelect>
                    <div
                      v-if="(errors.length > 0)"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <!-- <BCol
                v-if="state.editing"
                cols="12"
              >
                <BFormGroup>
                  <BFormCheckbox
                    id="fActive"
                    v-model="formDocument.active"
                    switch
                    value="1"
                    name="active"
                    unchecked-value="0"
                    :disabled="state.busy"
                  >
                    Is Active?
                  </BFormCheckbox>
                </BFormGroup>
              </BCol> -->
            </BRow>
          </form>
        </ValidationObserver>

        <attachment-uploader
          ref="attachmentUploader"
          :path="formDocument.attachment_path"
          :files="formDocument.attachments"
          :loading="state.busy"
          :show-uploader="!state.editing"
          @onUploaded="updateFormDocument"
        />
      </div>
      <template #modal-footer="{ cancel }">
        <BButton
          v-if="!state.editing"
          variant="primary"
          :disabled="state.busy || fetching.documentTypes"
          @click.prevent="validateDocument"
        >
          {{ state.editing ? 'Update' : 'Add' }} Document
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          {{ state.editing ? 'Close' : 'Discard' }}
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'

import AttachmentUploader from '@/views/asm/lead-management/documents/Uploader.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MDocumentTypes from '@/mixins/maintenances/document-types'

import { SASMLeadDocument } from '@/services/asm'

export default {
  name: 'VASMLeadDocuments',

  components: {
    'attachment-uploader': AttachmentUploader
  },

  mixins: [
    MDocumentTypes,
    MxFormatter,
    MxMisc
  ],

  props: {
    lead: {
      type: Object,
      required: true,
      default: () => ({
        id: ''
      })
    }
  },

  emits: ['updated'],

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formDocument: {
        lead_id: this.lead.id,
        document_type_id: '',
        attachment_path: '',
        attachments: [],
        active: 1
      },
      tableDocuments: {
        busy: false,
        filter: {
          search: '',
          lead: this.lead.id
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: [],
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 4, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 3, key: 'created_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 1, key: 'document_type.document_type_name', label: 'Document Type', class: 'min-w-150px' }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  watch: {
    'selected.documentType' (documentType) {
      this.formDocument.document_type_id = documentType?.id || null
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableDocuments.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        lead_id: filter.lead
      }

      return await SASMLeadDocument.get(payload).then(
        ({ data }) => {
          this.tableDocuments.totalRows = data.total_rows
          this.tableDocuments.rows = data.items
          return data.items
        }
      ).catch(
        () => {
          this.tableDocuments.totalRows = 0
          return []
        }
      ).finally(() => {
        this.$emit('updated', this.tableDocuments.rows)
        this.tableDocuments.busy = false
      })
    },

    // downloadConsent () {
    //   this.generatePDFLeadConsent().then(
    //     consentDocument => {
    //       consentDocument.download(`${this.lead.first_name} ${this.lead.last_name} - Consent.pdf`)
    //     }
    //   )
    // },

    createDocument () {
      this.state.editing = false

      this.editable.municipality = ''
      this.editable.barangay = ''

      this.formDocument = Object.assign(this.formDocument, {
        id: '',
        lead_id: this.lead.id,
        document_type_id: '',
        attachment_path: '',
        attachments: [],
        active: 1
      })

      this.getDocumentTypes(this)
      // .finally(() => {
      //   this.options.documentTypes = QUERY.filter(
      //     this.options.documentTypes,
      //     ({ id }) => Number(id) !== 1
      //   )
      // })

      this.$refs.modalDocument.show()
    },

    viewDocument (leadDocument) {
      this.state.editing = true

      this.editable.municipality = leadDocument.municipality_id
      this.editable.barangay = leadDocument.barangay_id

      this.formDocument = Object.assign(this.formDocument, {
        id: leadDocument.id,
        lead_id: this.lead.id,
        document_type_id: leadDocument.document_type_id,
        attachment_path: leadDocument.attachment_path,
        attachments: leadDocument.attachments,
        active: leadDocument.active
      })

      this.getDocumentTypes(this).finally(() => {
        this.selected.documentType = QUERY.find(this.options.documentTypes, {
          id: leadDocument.document_type_id
        }) || ''
      })

      this.$refs.modalDocument.show()
    },

    updateFormDocument (attachment) {
      this.formDocument = Object.assign(this.formDocument, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments
      })
    },

    async validateDocument () {
      await this.$refs.formDocument.validate().then(
        async validDocument => {
          const noAttachments = this.formDocument.attachments.length === 0

          if (!validDocument || noAttachments) {
            if (noAttachments) {
              this.$refs.attachmentUploader.setErrors({
                attachments: ['Please upload at least one attachment.']
              })
            }

            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update Lead Documents?</h6>' : '<h6>Add Documents to Lead?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Add',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? await this.putDocument() : await this.postDocument()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message.errors) {
                    this.$refs.attachmentUploader.setErrors(message.errors)
                    this.$refs.formDocument.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postDocument () {
      return await SASMLeadDocument.post(this.formDocument).then(
        ({ data: { message } }) => {
          this.$refs.modalDocument.hide()
          this.swalSuccess(message).then(() => {
            this.$refs.tableDocuments.refetch()
          })
        }
      )
    },

    async putDocument () {
      return await SASMLeadDocument.put(this.formDocument).then(
        ({ data: { message, lead_document: leadDocument } }) => {
          this.$refs.modalDocument.hide()
          this.swalSuccess(message).then(() => {
            this.$refs.tableDocuments.updateRow({
              id: leadDocument.id,
              lead_id: this.lead.id,
              document_type_id: leadDocument.document_type_id,
              attachment_path: leadDocument.attachment_path,
              attachments: leadDocument.attachments,
              active: leadDocument.active,
              updated_at: leadDocument.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
